import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";

import LaunchIcon from "@mui/icons-material/Launch";

const publicUrl = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
const constructionImage = `${publicUrl}/images/contruction.jpg`;
const logoSrc = `${publicUrl}/images/coc-logo.svg`;

function Construction() {
  useDocumentTitle("Maintenance in Progress | Vehicle for Hire Online Portal");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      {/* Skip link for keyboard users */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: "absolute",
          top: -40,
          left: 0,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          px: 2,
          py: 1,
          zIndex: 9999,
          textDecoration: "none",
          fontWeight: 600,
          borderRadius: "0 0 4px 0",
          "&:focus": {
            top: 0,
          },
        }}
      >
        Skip to main content
      </Box>

      {/* Header matching app branding */}
      <Box
        component="header"
        sx={{
          display: "flex",
          alignItems: "center",
          px: 3,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          minHeight: 56,
        }}
      >
        <Box
          component="img"
          src={logoSrc}
          alt="City of Calgary"
          sx={{
            height: 40,
            width: "auto",
            display: { xs: "none", sm: "block" },
          }}
          className="coc-logo-desktop"
        />
        <Box
          component="img"
          src={`${publicUrl}/images/simplified/coc-logo-simplify.svg`}
          alt="City of Calgary"
          sx={{
            height: 36,
            width: "auto",
            display: { xs: "block", sm: "none" },
          }}
          className="coc-logo-mobile"
        />
        <Typography
          component="span"
          variant="h6"
          sx={{
            ml: 2,
            color: "text.primary",
            fontWeight: 500,
            display: { xs: "none", md: "block" },
          }}
        >
          Vehicle for Hire Online Portal
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              ml: 1.5,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              bgcolor: "rgba(200, 16, 46, 0.08)",
              border: "1px solid rgba(200, 16, 46, 0.2)",
              verticalAlign: "middle",
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: "#c8102e",
                mr: 0.75,
              }}
              aria-hidden="true"
            />
            <Typography
              component="span"
              variant="caption"
              sx={{
                fontWeight: 600,
                color: "#c8102e",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                fontSize: "0.7rem",
                lineHeight: 1,
              }}
            >
              Maintenance
            </Typography>
          </Box>
        </Typography>
        <Typography
          component="span"
          variant="h6"
          sx={{
            ml: 2,
            color: "text.primary",
            fontWeight: 500,
            display: { xs: "block", md: "none" },
          }}
        >
          VFH-Portal
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              ml: 1,
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              bgcolor: "rgba(200, 16, 46, 0.08)",
              border: "1px solid rgba(200, 16, 46, 0.2)",
              verticalAlign: "middle",
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: "#c8102e",
                mr: 0.5,
              }}
              aria-hidden="true"
            />
            <Typography
              component="span"
              variant="caption"
              sx={{
                fontWeight: 600,
                color: "#c8102e",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                fontSize: "0.75rem",
                lineHeight: 1,
              }}
            >
              Maint
            </Typography>
          </Box>
        </Typography>
      </Box>

      {/* Main content */}
      <Box
        id="main-content"
        tabIndex={-1}
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#F7F7F7",
          px: { xs: 2, sm: 4 },
          pl: { md: 6 },
          pr: { md: 0 },
          overflow: { xs: "hidden", md: "visible" },
          position: "relative",
        }}
        role="main"
        aria-labelledby="maintenance-message"
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "center",
            gap: 4,
            zIndex: 1,
          }}
        >
          {/* Text content */}
          <Box
            sx={{
              maxWidth: 520,
              textAlign: "center",
              order: { xs: 2, md: 1 },
              position: "relative",
              zIndex: 2,
            }}
          >
            <Typography
              id="maintenance-message"
              component="h1"
              variant="h3"
              sx={{
                color: "text.primary",
                lineHeight: 1.2,
                fontWeight: 700,
                fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.5rem" },
                maxWidth: 520,
                mx: "auto",
                mb: 2,
              }}
            >
              The site is undergoing maintenance.
            </Typography>
            <Typography
              component="p"
              variant="body1"
              sx={{
                color: "text.secondary",
                lineHeight: 1.7,
                fontSize: { xs: "1rem", sm: "1.125rem" },
                maxWidth: 440,
                mx: "auto",
              }}
            >
              Please
              <Link
                href={process.env.REACT_APP_CONTACT_US_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: "inline-flex", alignItems: "center", ml: 0.5 }}
                aria-label="Contact Us (opens in new tab)"
              >
                Contact Us
                <LaunchIcon
                  sx={{ fontSize: "1rem", ml: 0.5 }}
                  aria-hidden="true"
                />
              </Link>{" "}

              if you have any questions.
            </Typography>
          </Box>

          {/* Illustration */}
          <Box
            component="img"
            src={constructionImage}
            alt=""
            aria-hidden="true"
            sx={{
              maxWidth: { xs: "95%", sm: "65%", md: "70%" },
              maxHeight: { xs: 360, md: "80vh" },
              objectFit: "contain",
              order: { xs: 1, md: 2 },
              flexShrink: 0,
              position: { xs: "static", md: "absolute" },
              right: { md: 0 },
              top: { md: "50%" },
              transform: { md: "translateY(-50%)" },
              zIndex: 1,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default Construction;
